import type { WorkflowItem } from "@/types/workflow"

/**
 * Substitutes field values in a URL template
 * Example: "abc.com/next/workOrder/${FboId}/detail" with FboId=123 becomes "abc.com/next/workOrder/123/detail"
 */
export function substituteFieldsInUrl(urlTemplate: string, row: WorkflowItem): string {
  return urlTemplate.replace(/\$\{([\w.]+)\}/g, (match, fieldPath) => {
    const value = getCaseInsensitivePath(row, fieldPath)
    if (value === null || value === undefined) {
      return match
    }
    return encodeURIComponent(String(value))
  })
}

/**
 * Evaluates a condition string against a work order item
 * Example: "${FboId} > 0 && ${Client.FboId} == 1234"
 */
export function evaluateCondition(condition: string, row: WorkflowItem): boolean {
  if (!condition || condition.trim() === '') {
    return true
  }

  try {
    const processedCondition = condition.replace(/\$\{([\w.]+)\}/g, (match, fieldPath) => {
      const value = getCaseInsensitivePath(row, fieldPath)

      if (value === null || value === undefined) {
        return 'null'
      }

      // Arrays substitute as a JS boolean reflecting non-emptiness so
      // conditions like `${SomeList}` or `${SomeList} == true` correctly
      // gate on "the list has entries". The previous behaviour (any
      // object → literal 'true') made empty arrays indistinguishable
      // from populated ones — useless for action-config gates like
      // "show ViewDuplicates only when PossibleDuplicateOfRequestIds
      // is non-empty". Checked before the object branch because
      // typeof [] === 'object' in JS.
      if (Array.isArray(value)) {
        return value.length > 0 ? 'true' : 'false'
      }

      if (typeof value === 'object') {
        return 'true'
      }

      if (typeof value === 'string') {
        return `'${value.replace(/'/g, "\\'")}'`
      }

      if (typeof value === 'boolean') {
        return value.toString()
      }

      if (typeof value === 'number') {
        return value.toString()
      }

      return `'${String(value).replace(/'/g, "\\'")}'`
    })

    const result = new Function('return ' + processedCondition)()
    return Boolean(result)
  } catch (error) {
    console.warn('Failed to evaluate condition:', condition, error)
    return false
  }
}

/**
 * Resolves a dot-notation path against an object with case-insensitive key matching at each level.
 * Examples: "FboId" → row.fboId, "Client.FboId" → row.client.fboId
 */
function getCaseInsensitivePath(obj: Record<string, any>, path: string): any {
  if (!obj || !path) return undefined

  const parts = path.split('.')
  let current: any = obj

  for (const part of parts) {
    if (current === null || current === undefined || typeof current !== 'object') {
      return undefined
    }

    if (current[part] !== undefined) {
      current = current[part]
      continue
    }

    const lowerPart = part.toLowerCase()
    const foundKey = Object.keys(current).find(k => k.toLowerCase() === lowerPart)
    if (foundKey !== undefined) {
      current = current[foundKey]
    } else {
      return undefined
    }
  }

  return current
}

/**
 * Validates and processes external sources for a given work order item
 */
export interface ProcessedExternalSource {
  titleEnglish: string
  titleFrench: string
  url: string
  originalLink: string
  condition?: string
  conditionMet: boolean
}

export function processExternalSources(
  externalSources: any[],
  row: WorkflowItem
): ProcessedExternalSource[] {
  return externalSources
    .map((source): ProcessedExternalSource | null => {
      if (!source?.link || typeof source.link !== 'string') {
        return null
      }

      const conditionMet = source.condition 
        ? evaluateCondition(source.condition, row)
        : true

      if (!conditionMet) {
        return null
      }

      const url = substituteFieldsInUrl(source.link, row)

      return {
        titleEnglish: source.titleEnglish || '',
        titleFrench: source.titleFrench || source.titleEnglish || '',
        url,
        originalLink: source.link,
        condition: source.condition,
        conditionMet: true,
      }
    })
    .filter((source): source is ProcessedExternalSource => source !== null)
}

